import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useWorkerApplicationsList, WORKER_APPLICATIONS_DEFAULT_PAGE_SIZE } from "./useWorkerApplicationsList.js";
import { getWorkerApplication } from "./workerApplicationsApi.js";
import WorkerDetailedProfileDialog from "../workerDetailedProfile/WorkerDetailedProfileDialog.jsx";
import "./workerApplicationInbox.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";
import RecordDrawerHeader from "../components/RecordDrawerHeader.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "SuperAdmin", "Worker Intake", "Invite / Applications"];
const DRAWER_TITLE_ID = "wa-drawer-title";
const DEFAULT_STATUS_FILTER = "QuickRegistered";
const SEARCH_DEBOUNCE_MS = 350;

// Fixed UI copy describing the well-known Quick Register -> e-KYC -> Ready
// for Match business rule (Prompt 093/094B) - not per-application data, so
// it is safe to hardcode exactly like the approved W44 reference does.
const NOT_READY_FOR_MATCH_TEXT = "ยังไม่พร้อม match";
const REQUIRES_EKYC_TEXT = "ต้องผ่าน e-KYC";
const NO_RAW_INVITE_LINK_TEXT = "ไม่แสดง invite link เต็มในหน้านี้";
const CANNOT_SEND_TO_EMPLOYER_TEXT = "ยังไม่สามารถส่งให้นายจ้าง match ได้";
const START_EKYC_BUTTON_TEXT = "เริ่ม e-KYC";

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูใบสมัครนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบใบสมัครนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดใบสมัครได้ กรุณาลองใหม่อีกครั้ง";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function formatAgeHours(ageHours) {
  if (ageHours === null || ageHours === undefined) {
    return "-";
  }
  if (ageHours < 1) {
    return "เมื่อครู่";
  }
  if (ageHours < 24) {
    return `${ageHours} ชม.ที่แล้ว`;
  }
  return `${Math.floor(ageHours / 24)} วันที่แล้ว`;
}

/** "YYYY-MM-DD" (native date input value) -> UTC day-boundary ISO string. */
function dateInputToIsoBoundary(value, boundary) {
  if (!value) {
    return "";
  }
  return boundary === "end" ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`;
}

/**
 * /backoffice/worker-intake/invite-applications - the Worker Application
 * Inbox (Prompt 094B, built from the W44 reference). Read-only backoffice
 * view over the Prompt 093 API for workers who completed Quick Register
 * through an Invite Card link but have not yet passed e-KYC. This screen
 * never creates a WmxWorkers row, never runs e-KYC, and never marks
 * anything ready for match - stage.readyForMatch is always false in this
 * packet, and the "next step" action is always disabled/reserved.
 *
 * Filtering/pagination is real server-side (not a client-side filter over
 * one big fetch, unlike SuperAgentsOverviewPage) - this table can hold far
 * more rows than this app's other list pages, so every filter change is a
 * new GET with query params, and free-text search is debounced so it does
 * not fire a request per keystroke.
 */
export default function WorkerApplicationInboxPage() {
  const { data: identity } = useBackofficeIdentity();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [nationalityFilter, setNationalityFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [page, setPage] = useState(1);
  const [workerRecordApplicationId, setWorkerRecordApplicationId] = useState(null);

  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  // Debounce free-text search (Prompt 094B: at least 300ms) before it ever
  // becomes part of the fetch filters - typing does not fire one request
  // per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any real filter change (not a page change) returns to page 1 - a stale
  // page number from a previous, more specific filter would otherwise look
  // like "no results" instead of resetting to the start of the new set.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, nationalityFilter, languageFilter, agentFilter, dateFromFilter, dateToFilter]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: WORKER_APPLICATIONS_DEFAULT_PAGE_SIZE,
      status: statusFilter,
      nationality: nationalityFilter,
      preferredLanguageCode: languageFilter,
      agentId: agentFilter,
      dateFromUtc: dateInputToIsoBoundary(dateFromFilter, "start"),
      dateToUtc: dateInputToIsoBoundary(dateToFilter, "end"),
      search,
    }),
    [page, statusFilter, nationalityFilter, languageFilter, agentFilter, dateFromFilter, dateToFilter, search]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useWorkerApplicationsList(filters);

  // Detail is fetched only when a row is opened, and only refetched for the
  // same open row when Refresh is explicitly pressed (detailReloadToken) -
  // never on an interval, never speculatively.
  useEffect(() => {
    if (!selectedApplicationId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedApplicationId ? previous.data : null,
      error: null,
      forId: selectedApplicationId,
    }));

    getWorkerApplication(selectedApplicationId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedApplicationId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedApplicationId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedApplicationId
  }, [selectedApplicationId, detailReloadToken]);

  function openDrawer(applicationId) {
    setSelectedApplicationId(applicationId);
  }

  function closeDrawer() {
    setSelectedApplicationId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedApplicationId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  // P146: the detailed-profile editor can correct the applicant's own name /
  // nationality / phone / skill on this very application row, so a save has
  // to re-pull both the drawer and the list behind it - otherwise the row
  // keeps showing the pre-edit values until the next manual refresh. Reuses
  // the exact same two calls the Refresh button already makes.
  function handleProfileSaved() {
    handleRefresh();
  }

  const items = listData?.items ?? [];
  const summary = listData?.summary ?? null;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / WORKER_APPLICATIONS_DEFAULT_PAGE_SIZE));
  const topNationality = summary?.byNationality?.[0] ?? null;
  const hasActiveFilters = Boolean(
    search || nationalityFilter || languageFilter || agentFilter || dateFromFilter || dateToFilter || statusFilter !== DEFAULT_STATUS_FILTER
  );

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดรายการใบสมัคร Quick Register..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการใบสมัครได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-wa-page">
        <div className="wx-wa-toolbar">
          <label className="wx-wa-search">
            <span className="wx-visually-hidden">ค้นหาชื่อ เบอร์โทร Agent หรือรหัสใบสมัคร</span>
            <input
              type="search"
              placeholder="ค้นหาชื่อ เบอร์โทร Agent หรือรหัสใบสมัคร"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-wa-filters">
            <label className="wx-wa-filter">
              <span>สถานะ</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                <option value="QuickRegistered">Quick Registered</option>
              </select>
            </label>

            <label className="wx-wa-filter">
              <span>ประเทศ/สัญชาติ</span>
              <select value={nationalityFilter} onChange={(event) => setNationalityFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {(summary?.byNationality ?? []).map((entry) => (
                  <option key={entry.nationality} value={entry.nationality}>
                    {entry.nationality} ({entry.count})
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-wa-filter">
              <span>ภาษา</span>
              <input
                type="text"
                placeholder="เช่น bn"
                value={languageFilter}
                onChange={(event) => setLanguageFilter(event.target.value)}
              />
            </label>

            <label className="wx-wa-filter">
              <span>Agent</span>
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {(summary?.byAgent ?? []).map((entry) => (
                  <option key={entry.agentId} value={entry.agentId}>
                    {entry.agentCode} · {entry.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-wa-filter wx-wa-filter--date">
              <span>วันที่สมัคร</span>
              <span className="wx-wa-date-range">
                <input type="date" value={dateFromFilter} onChange={(event) => setDateFromFilter(event.target.value)} />
                <span aria-hidden="true">-</span>
                <input type="date" value={dateToFilter} onChange={(event) => setDateToFilter(event.target.value)} />
              </span>
            </label>
          </div>

          <div className="wx-wa-toolbar-actions">
            {/* wx-wa-refresh-button is also listed in DetailDrawer's
                selectableSelector below - without it, clicking Refresh while
                a row is open is an "outside click" as far as the drawer's
                own close-on-outside-click listener is concerned, which
                would close the drawer instead of refreshing its content. */}
            <button type="button" className="wx-button wx-button--ghost wx-wa-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            <button type="button" className="wx-button wx-button--ghost" disabled title="ยังไม่เปิดใช้งาน">
              Export
            </button>
          </div>
        </div>

        <header className="wx-wa-head">
          <div className="wx-wa-title">
            <h1>Invite / Applications</h1>
            <p>รายการแรงงานที่กรอก Quick Register ผ่าน Invite Card แล้ว แต่ยังต้องผ่าน e-KYC ก่อนจึงจะพร้อมส่งให้นายจ้าง</p>
          </div>
          {identity?.primaryRole && (
            <span className="wx-wa-scope-pill">
              Scope <b>{identity.primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-wa-metrics">
          <MetricCard label="QUICK REGISTERED" value={summary?.quickRegisteredCount} note="รอ Agent ตรวจสอบ" />
          <MetricCard label="AWAITING E-KYC" value={summary?.awaitingEkycCount} note="ยังไม่ผ่านยืนยันตัวตน" variant="amber" />
          <MetricCard label="REGISTERED TODAY" value={summary?.todayCount} note="สมัครเข้ามาวันนี้" variant="green" />
          <MetricCard
            label="OLDEST PENDING"
            value={summary?.oldestPendingHours != null ? `${summary.oldestPendingHours} ชม.` : "-"}
            note="รอนานที่สุดในคิว"
            variant="red"
          />
          <MetricCard
            label="TOP NATIONALITY"
            value={topNationality?.nationality ?? "-"}
            note={topNationality ? `${topNationality.count} จาก ${totalCount} ราย` : "ยังไม่มีข้อมูล"}
          />
        </div>

        <section className="wx-panel wx-wa-list-panel">
          <div className="wx-wa-list-head">
            <h2 className="wx-panel-title">รายการใบสมัคร Quick Register</h2>
            <span className="wx-wa-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-wa-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">
              {hasActiveFilters ? "ไม่พบใบสมัครที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มีใบสมัคร Quick Register ในระบบ"}
            </p>
          ) : (
            <>
              <div className="wx-wa-table-wrap">
                <table className="wx-wa-table">
                  <colgroup>
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "10%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>ใบสมัคร</th>
                      <th>แรงงาน</th>
                      <th>ติดต่อ</th>
                      <th>Agent ผู้ดูแล</th>
                      <th>สายงาน</th>
                      <th>Invite Card</th>
                      <th>Stage</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.applicationId} className="wx-wa-row" onClick={() => setWorkerRecordApplicationId(item.applicationId)}>
                        <td>
                          <span className="wx-wa-cell-strong">{item.applicationId}</span>
                          <span className="wx-wa-cell-sub">{formatAgeHours(item.ageHours)}</span>
                        </td>
                        <td>
                          <span className="wx-wa-cell-strong">
                            {item.fullNameNative || item.fullNameEnglish || "-"}
                            {item.fullNameNative && item.fullNameEnglish ? ` (${item.fullNameEnglish})` : ""}
                          </span>
                          <span className="wx-wa-cell-sub">{item.nationality ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-wa-cell-strong">{item.phoneNumber ?? "-"}</span>
                          <span className="wx-wa-cell-sub">ภาษา: {item.preferredLanguageCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-wa-cell-strong">{item.agent?.agentCode ?? "-"}</span>
                          <span className="wx-wa-cell-sub">{item.agent?.displayName ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-wa-cell-strong">{item.line?.superAgentName ?? "-"}</span>
                          <span className="wx-wa-cell-sub">{item.line?.plcName ?? "-"}</span>
                        </td>
                        <td>
                          {item.inviteCard ? (
                            <>
                              <span className="wx-wa-cell-code">{item.inviteCard.inviteCodeMasked}</span>
                              <span className="wx-wa-cell-sub">ปลายทาง: {item.inviteCard.intendedCountryCode ?? "-"}</span>
                            </>
                          ) : (
                            <span className="wx-wa-cell-sub">-</span>
                          )}
                        </td>
                        <td>
                          <StageBadges item={item} />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="wx-wa-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDrawer(item.applicationId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-wa-cards">
                {items.map((item) => (
                  <WorkerApplicationCard key={item.applicationId} item={item} onOpen={() => openDrawer(item.applicationId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer
        open={selectedApplicationId !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-wa-row, .wx-wa-card, .wx-wa-refresh-button"
      >
        <WorkerApplicationDetail state={detailState} titleId={DRAWER_TITLE_ID} onProfileSaved={handleProfileSaved} />
      </DetailDrawer>
      
        <WorkerRecordDrawer
        open={workerRecordApplicationId !== null}
        applicationId={workerRecordApplicationId}
        onClose={() => setWorkerRecordApplicationId(null)}
        selectableSelector="tr.wx-wa-row, .wx-wa-card"
      />
    </AppShell>
  );
}

function MetricCard({ label, value, note, reservedNote, variant }) {
  const variantClass = variant ? ` wx-wa-metric--${variant}` : "";
  return (
    <div className={`wx-wa-metric${reservedNote ? " wx-wa-metric--reserved" : ""}${variantClass}`}>
      <span className="wx-wa-metric-label">{label}</span>
      {reservedNote ? (
        <span className="wx-wa-metric-note">{reservedNote}</span>
      ) : (
        <>
          <strong className="wx-wa-metric-value">{value ?? "-"}</strong>
          {note && <span className="wx-wa-metric-note">{note}</span>}
        </>
      )}
    </div>
  );
}

// Short table/card badge text per real stage.code - a compact alias for the
// backend's own (much longer) full sentence, which is still shown verbatim
// in the drawer detail (see WorkerApplicationDetail). Only one code exists
// today; an unrecognized future code falls back to its own real label
// rather than inventing a new short phrase for it.
const STAGE_SHORT_LABELS = { AwaitingEkyc: "รอ e-KYC" };

/**
 * Two badges only, both derived from real response fields: applicationStatus
 * is always "QuickRegistered" so far, and the second badge is a short alias
 * of the backend's own stage.label - never a per-row invented variant like
 * "needs follow-up" that the data does not actually distinguish.
 */
function StageBadges({ item }) {
  const shortLabel = STAGE_SHORT_LABELS[item.stage?.code] ?? item.stage?.label ?? "รอ e-KYC";
  return (
    <div className="wx-wa-stage-badges">
      <span className="wx-badge wx-badge--info">Quick Register</span>
      <span className="wx-badge wx-badge--warning">{shortLabel}</span>
    </div>
  );
}

function WorkerApplicationCard({ item, onOpen }) {
  return (
    <div className="wx-wa-card" onClick={onOpen}>
      <div className="wx-wa-card-top">
        <div>
          <strong>{item.fullNameNative || item.fullNameEnglish || "-"}</strong>
          <span>
            {item.applicationId} · {item.nationality ?? "-"}
          </span>
        </div>
        <StageBadges item={item} />
      </div>
      <div className="wx-wa-card-grid">
        <div>
          <label>ติดต่อ</label>
          <strong>{item.phoneNumber ?? "-"}</strong>
        </div>
        <div>
          <label>ภาษา</label>
          <strong>{item.preferredLanguageCode ?? "-"}</strong>
        </div>
        <div>
          <label>Agent ผู้ดูแล</label>
          <strong>
            {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
          </strong>
        </div>
        <div>
          <label>สายงาน</label>
          <strong>
            {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
          </strong>
        </div>
        <div>
          <label>Invite Card</label>
          <strong className="wx-wa-cell-code">{item.inviteCard?.inviteCodeMasked ?? "-"}</strong>
        </div>
        <div>
          <label>สมัครเมื่อ</label>
          <strong>{formatAgeHours(item.ageHours)}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-wa-row-action wx-wa-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

/**
 * P146: the SuperAdmin/SysAdmin-facing entry point into the detailed worker
 * profile editor, placed directly above the Quick Register readout it
 * completes - the officer reading "this is all the worker gave us" needs the
 * way to fill in the rest right there, not at the bottom of the drawer.
 *
 * Opens the same shared dialog the Agent Portal's applicant drawer uses, over
 * this drawer, so the officer never loses their filtered inbox position. The
 * dialog resolves edit permission from the backend's own canEdit flag; this
 * page does not gate the button by role, because the server is the authority.
 */
function WorkerDetailedProfileAction({ applicationId, onSaved }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <section className="wx-wa-dsec">
      <h3>โปรไฟล์แรงงานแบบละเอียด</h3>
      <p className="wx-wa-dsec-note">
        เติมข้อมูลตัวตน เอกสาร ทักษะ และพื้นที่ที่ทำงานได้ เพื่อให้ใช้จับคู่งานได้จริง โดยไม่เปลี่ยนสถานะ e-KYC
      </p>
      <button type="button" className="wx-button wx-button--primary" onClick={() => setDialogOpen(true)}>
        แก้ไขข้อมูลแรงงาน
      </button>
      <WorkerDetailedProfileDialog
        open={dialogOpen}
        applicationId={applicationId}
        onClose={() => setDialogOpen(false)}
        onSaved={onSaved}
      />
    </section>
  );
}

function WorkerApplicationDetail({ state, titleId, onProfileSaved }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียดใบสมัคร..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-wa-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;

  // Real timeline entries the API returned, followed by the current
  // in-progress stage (from stage.code/label - not yet in timeline[] since
  // it has no occurredAtUtc of its own), followed by a fixed locked "Ready
  // for Match" step. Only the last entry's label/note is UI-only copy
  // describing the known, unimplemented gate - never a fabricated
  // timestamp or per-application variation.
  const timelineSteps = [
    ...(detail.timeline ?? []).map((entry) => ({ key: entry.stageCode, label: entry.label, state: "done" })),
    { key: detail.stage?.code ?? "current", label: detail.stage?.label ?? REQUIRES_EKYC_TEXT, state: "current" },
    { key: "ReadyForMatch", label: "Ready for Match — ล็อกอยู่", note: "จะปลดล็อกหลังผ่าน e-KYC เท่านั้น", state: "locked" },
  ];

  return (
    <div className="wx-wa-drawer">
      {/* The applicant is a worker, so the drawer reads like Worker Profiles:
          their name first, the application id under it. */}
      <RecordDrawerHeader
        titleId={titleId}
        avatarText={detail.fullNameNative || detail.fullNameEnglish || detail.applicationId}
        title={detail.fullNameNative || detail.fullNameEnglish || "-"}
        subtitle={detail.applicationId}
      />
      <div className="wx-wa-drawer-status">
        <span className="wx-badge wx-badge--info">Quick Register</span>
        <span className="wx-badge wx-badge--warning">{REQUIRES_EKYC_TEXT}</span>
        {/* Fixed for this packet, not gated on detail.stage.readyForMatch -
            e-KYC/matching are not implemented here regardless of what any
            future backend value might say, so this is never suppressed. */}
        <span className="wx-badge wx-badge--danger">{NOT_READY_FOR_MATCH_TEXT}</span>
      </div>

      <WorkerDetailedProfileAction applicationId={detail.applicationId} onSaved={onProfileSaved} />

      <section className="wx-wa-dsec">
        <h3>ข้อมูล Quick Register ของแรงงาน</h3>
        <div className="wx-wa-kv-row">
          <span>ชื่อ</span>
          <strong>
            {detail.fullNameNative || detail.fullNameEnglish || "-"}
            {detail.fullNameNative && detail.fullNameEnglish ? ` (${detail.fullNameEnglish})` : ""}
          </strong>
        </div>
        <div className="wx-wa-kv-row">
          <span>สัญชาติ</span>
          <strong>{detail.nationality ?? "-"}</strong>
        </div>
        <div className="wx-wa-kv-row">
          <span>เบอร์โทร</span>
          <strong>{detail.phoneNumber ?? "-"}</strong>
        </div>
        <div className="wx-wa-kv-row">
          <span>ภาษาที่ต้องการ</span>
          <strong>{detail.preferredLanguageCode ?? "-"}</strong>
        </div>
        <div className="wx-wa-kv-row">
          <span>วันที่สมัคร</span>
          <strong>{formatDateTimeTh(detail.createdAtUtc) ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-wa-dsec">
        <h3>Agent และสายงาน</h3>
        <div className="wx-wa-kv-row">
          <span>Agent</span>
          <strong>
            {detail.agent?.agentCode ?? "-"} · {detail.agent?.displayName ?? "-"}
          </strong>
        </div>
        <div className="wx-wa-kv-row">
          <span>Super Agent</span>
          <strong>{detail.line?.superAgentName ?? "-"}</strong>
        </div>
        <div className="wx-wa-kv-row">
          <span>PLC / บมจ</span>
          <strong>{detail.line?.plcName ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-wa-dsec wx-wa-dsec--highlight">
        <h3>Invite Card ที่ใช้สมัคร</h3>
        {detail.inviteCard ? (
          <>
            <div className="wx-wa-kv-row">
              <span>รหัส Invite</span>
              <strong className="wx-wa-cell-code">{detail.inviteCard.inviteCodeMasked}</strong>
            </div>
            <div className="wx-wa-kv-row">
              <span>ประเทศ/ภาษาปลายทาง</span>
              <strong>
                {detail.inviteCard.intendedCountryCode ?? "-"} · {detail.inviteCard.intendedLanguageCode ?? "-"}
              </strong>
            </div>
            {detail.inviteCard.noteToWorker && (
              <div className="wx-wa-kv-row">
                <span>Note ถึงแรงงาน</span>
                <strong>{detail.inviteCard.noteToWorker}</strong>
              </div>
            )}
          </>
        ) : (
          <p className="wx-state-body">ไม่พบข้อมูล Invite Card สำหรับใบสมัครนี้</p>
        )}
        <p className="wx-wa-notice">{NO_RAW_INVITE_LINK_TEXT}</p>
      </section>

      <section className="wx-wa-dsec">
        <h3>สถานะขั้นตอน</h3>
        <ol className="wx-wa-timeline">
          {timelineSteps.map((step) => (
            <li key={step.key} className={`wx-wa-timeline-step wx-wa-timeline-step--${step.state}`}>
              <span className="wx-wa-timeline-dot" aria-hidden="true" />
              <div>
                <strong>{step.label}</strong>
                {step.note && <small>{step.note}</small>}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="wx-wa-dsec">
        <h3>ขั้นตอนถัดไป</h3>
        {/* Unconditionally disabled, no onClick - this packet never executes
            e-KYC, so there is nothing for an "enabled" state to do even if a
            future backend response ever set nextStep.enabled to true. */}
        <button type="button" className="wx-button wx-button--primary wx-wa-ekyc-button" disabled>
          {START_EKYC_BUTTON_TEXT}
        </button>
        <p className="wx-wa-note-muted">{detail.nextStep?.label ?? "ยังไม่เปิดใช้งาน"}</p>
        <p className="wx-wa-warning">{CANNOT_SEND_TO_EMPLOYER_TEXT}</p>
      </section>
    </div>
  );
}
